/**
 * Add-by-type / AFH checkout can leave a selectable row without a
 * UserAssociatedFacility. Individual dashboard then 403s:
 * {"detail":"User does not have access to this facility"}.
 * Repair by POSTing create-from-ccn (slug-only, skip CCN validation).
 */

import { createFacilityFromCcn } from "@services/facilityManagement";
import { getFacilityTypes } from "@services/facilitytype";
import { getCurrentUser } from "@utils/userUtils";
import { isRulesSafeFacilityIdentifier } from "@utils/facilityUtils";
import { resolveAddFacilityByTypeFromCatalog } from "./addFacilityByType";
import {
  isHubSynthesizedSelfReportedRow,
  isSelfReportedPreferredFacility,
  preferredDetailsRecord,
  resolveCheckoutPreferredFacilityPk,
  selfReportedFacilityTypeSlug,
  selfReportedLicenseNumber,
  selfReportedUniqueId,
} from "./selfReportedPreferredFacility";

const ADD_BY_TYPE_SLUGS = new Set([
  "adult_family_home",
  "assisted_living_facility",
  "memory_care",
  "independent_living",
]);

function asRecord(value: unknown): Record<string, unknown> {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return {};
}

function nonemptyString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function finiteId(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function dashboardAccessDeniedMessage(error: unknown): boolean {
  const raw = error instanceof Error ? error.message : String(error ?? "");
  return /does not have access to this facility/i.test(raw);
}

export function hasAddByTypeProfileSlug(facility: unknown): boolean {
  const details = preferredDetailsRecord(facility);
  const row = asRecord(facility);
  const targeting = asRecord(row.profile_targeting_json || details.profile_targeting_json);
  const slug = catalogSlugKey(
    selfReportedFacilityTypeSlug(facility) ||
      nonemptyString(row.primary_profile_slug) ||
      nonemptyString(details.primary_profile_slug) ||
      nonemptyString(targeting.primary_profile_slug) ||
      nonemptyString(targeting.facility_type_slug),
  );
  return ADD_BY_TYPE_SLUGS.has(slug);
}

function catalogSlugKey(value: string): string {
  return value.toLowerCase().replace(/-/g, "_");
}

/** Repair dashboard 403 for add-by-type / slug-only AFH (type id often missing). */
export function shouldRepairFacilityDashboardAccess(
  error: unknown,
  facility: unknown,
): boolean {
  if (!dashboardAccessDeniedMessage(error)) return false;
  return (
    isSelfReportedPreferredFacility(facility) ||
    isHubSynthesizedSelfReportedRow(facility) ||
    hasAddByTypeProfileSlug(facility)
  );
}

export function buildSelfReportedCreateFromCcnPayload(facility: unknown): {
  ccn_number?: string;
  facility_name: string;
  city: string;
  state: string;
  country: string;
  facility_type: string;
  facility_type_id?: number;
  username: string;
  pricing_tier_id: number;
  phone_number?: string;
  skip_ccn_validation: boolean;
  address_1?: string;
  address_2?: string;
  postal_code?: string;
  vertical?: string;
  primary_profile_slug?: string;
  profile_targeting_json?: Record<string, unknown>;
} | null {
  if (!facility || typeof facility !== "object") return null;
  const details = preferredDetailsRecord(facility);
  const row = asRecord(facility);
  const name =
    nonemptyString(row.facility_name) ||
    nonemptyString(details.facility_name) ||
    nonemptyString(details.site_name);
  const city = nonemptyString(row.city) || nonemptyString(details.city);
  const state = nonemptyString(row.state) || nonemptyString(details.state);
  if (!name || !city || !state) return null;

  const slug = catalogSlugKey(
    selfReportedFacilityTypeSlug(facility) ||
      nonemptyString(row.primary_profile_slug) ||
      nonemptyString(details.primary_profile_slug),
  );
  const typeName =
    nonemptyString(row.facility_type) ||
    nonemptyString(details.facility_type) ||
    (slug ? slug.replace(/_/g, " ") : "Facility");
  const typeId =
    finiteId(row.facility_type_id) ?? finiteId(details.facility_type_id);
  const license = selfReportedLicenseNumber(facility);
  const upfPk = resolveCheckoutPreferredFacilityPk(facility);
  const uniqueFromLicense = selfReportedUniqueId(upfPk, license);
  const storedCcn =
    nonemptyString(row.ccn_number) || nonemptyString(details.ccn_number);
  const ccn_number = uniqueFromLicense
    ? uniqueFromLicense
    : isRulesSafeFacilityIdentifier(storedCcn)
      ? storedCcn
      : upfPk
        ? selfReportedUniqueId(upfPk, "")
        : "";

  const user = getCurrentUser();
  const username =
    (user?.first_name && user?.last_name
      ? `${user.first_name} ${user.last_name}`
      : user?.display_name || user?.first_name || user?.email || "user") || "user";

  const targeting = asRecord(
    row.profile_targeting_json || details.profile_targeting_json,
  );
  if (slug && !targeting.facility_type_slug) targeting.facility_type_slug = slug;
  if (slug && !targeting.primary_profile_slug) targeting.primary_profile_slug = slug;
  targeting.skip_npi_validation = true;
  targeting.verification_source = "self_reported";

  const country =
    nonemptyString(row.country_code) ||
    nonemptyString(row.country) ||
    nonemptyString(details.country) ||
    "USA";

  return {
    ...(ccn_number ? { ccn_number } : {}),
    facility_name: name,
    city,
    state,
    country: country.length === 3 ? country.toUpperCase() : "USA",
    facility_type: typeName,
    ...(typeId != null ? { facility_type_id: typeId } : {}),
    username,
    pricing_tier_id: finiteId(details.pricing_tier_id) ?? 1,
    phone_number:
      nonemptyString(row.phone_number) ||
      nonemptyString(details.phone_number) ||
      undefined,
    skip_ccn_validation: true,
    address_1:
      nonemptyString(row.address_1) || nonemptyString(details.address_1) || undefined,
    address_2:
      nonemptyString(row.address_2) || nonemptyString(details.address_2) || undefined,
    postal_code:
      nonemptyString(row.postal_code) ||
      nonemptyString(details.postal_code) ||
      undefined,
    vertical:
      nonemptyString(row.vertical) ||
      nonemptyString(details.vertical) ||
      "healthcare",
    ...(slug ? { primary_profile_slug: slug } : {}),
    profile_targeting_json: targeting,
  };
}

export async function ensureSelfReportedFacilityEntitlement(
  facility: unknown,
): Promise<{ facilityId: number | null }> {
  const payload = buildSelfReportedCreateFromCcnPayload(facility);
  if (!payload) return { facilityId: null };
  if (payload.facility_type_id == null && payload.primary_profile_slug) {
    try {
      const catalog = await getFacilityTypes();
      const resolved = resolveAddFacilityByTypeFromCatalog(
        payload.primary_profile_slug,
        catalog.facility_types,
      );
      if (resolved?.facility_type_id != null) {
        payload.facility_type_id = resolved.facility_type_id;
      }
    } catch {
      /* Rules can still accept slug-only after the companion fix. */
    }
  }
  const result = await createFacilityFromCcn(payload);
  return { facilityId: finiteId(result.facility_id) };
}
